import { ParamType, type InputParam } from "../types/grid.ts";

const key = (f: InputParam) => (f.sColumn.startsWith("@") ? f.sColumn.substring(1) : f.sColumn).toLowerCase();
const isColumnFilter = (f: InputParam) => f.Type !== ParamType.Specified;

/** The filters one grid request sends: the page's fixed (static) filters, then the
 *  parameter filters, then the user's filters. The server declares one SQL variable
 *  per column filter, so a column sent twice fails ("The variable name '@x' has
 *  already been declared"). A static filter scopes the grid (a kiosk, a customer,
 *  the kiosks behind a count), so it wins: a grid filter on the same column is
 *  dropped. Specified (procedure parameter) filters are left alone — the server
 *  just sets the parameter again, and pages rely on the user's value replacing a
 *  static default. */
export function mergeGridFilters(
  staticFilters: InputParam[] | undefined,
  paramFilters: InputParam[] | undefined,
  userFilters: InputParam[],
): InputParam[] {
  const fixed = staticFilters ?? [];
  const taken = new Set(fixed.filter(isColumnFilter).map(key));
  const rest = [...(paramFilters ?? []), ...userFilters].filter((f) => !(isColumnFilter(f) && taken.has(key(f))));
  return [...fixed, ...rest];
}

/** Remap filter sColumn names using MigratedFilters (newCol → oldCol). */
export function applyMigratedFilters(
  filters: InputParam[],
  migratedFilters?: Record<string, string | null> | null,
): InputParam[] {
  if (!migratedFilters) return filters;
  return filters.map(f => {
    const mapped = migratedFilters[f.sColumn.toUpperCase()];
    if (typeof mapped === "string") return { ...f, sColumn: mapped };
    return f;
  });
}

/** The FilterList a grid sends: the data fetch, the count and the export all build
 *  it here, so an export covers exactly the rows the grid shows (a popup's or a
 *  kiosk tab's static scope included). */
export function gridRequestFilters(
  staticFilters: InputParam[] | undefined,
  paramFilters: InputParam[] | undefined,
  userFilters: InputParam[],
  migratedFilters?: Record<string, string | null> | null,
): InputParam[] {
  return applyMigratedFilters(mergeGridFilters(staticFilters, paramFilters, userFilters), migratedFilters);
}
